import React, { useState, useEffect, useRef } from 'react';
import LoginCheck from '../pages/logincheck';
import Prism from 'prismjs';
import 'prismjs/themes/prism-okaidia.css'; // Choose a theme you like
import 'prismjs/components/prism-sql'; // Import SQL language support
import useWebSocket from 'react-use-websocket';

const API_BASE_URL = 'https://c997e1db73cd.ngrok.app';
const API_KEY = 'f8aa3f2b-bc46-4ce8-ab48-b7574344a9dd'
const WS_URL = 'wss://6aiolymuhf.execute-api.us-east-2.amazonaws.com/prod';

const AIQueryAgent = () => {
    const [sqlQuery, setSqlQuery] = useState('');
    const [highlightedHtml, setHighlightedHtml] = useState(''); // State for the highlighted HTML string
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [naturalLanguageQuery, setNaturalLanguageQuery] = useState('');
    const [isTranslating, setIsTranslating] = useState(false);
    const [translationError, setTranslationError] = useState(null);
    const [translationSuccess, setTranslationSuccess] = useState(''); // New state for success message
    const [isSqlAreaFlashing, setIsSqlAreaFlashing] = useState(false); // State for SQL area flash
    const [isSqlTextareaDisabled, setIsSqlTextareaDisabled] = useState(false); // New state for SQL textarea disabled status
    const [executionSuccess, setExecutionSuccess] = useState(''); // New state for execution success message
    const textareaRef = useRef(null);
    const naturalLanguageQueryTextareaRef = useRef(null); // Add a ref for the natural language query textarea
    const preRef = useRef(null); // Ref for the <pre> element
    const [userId] = useState(localStorage.getItem('user_id')); // Added for WebSocket

    // WebSocket connection
    const { getWebSocket } = useWebSocket(WS_URL + '?sourceSystemUrl=' + window.location.href + '&userId=' + userId, {
        onOpen: () => {
            console.log('Connected as ' + userId);
        },
        onMessage: (event) => {
            console.log('Received WebSocket message:', event.data);
            try {
                const rawEventData = JSON.parse(event.data); // Outer WS message
                let dataToProcess = rawEventData.message; // Actual payload is expected here

                // If dataToProcess is a string, it might be a JSON string needing parsing,
                // or a simple text message (e.g., a direct error string).
                if (typeof dataToProcess === 'string') {
                    try {
                        dataToProcess = JSON.parse(dataToProcess); // Attempt to parse if it's a JSON string
                    } catch (e) {
                        // If parsing dataToProcess (rawEventData.message) as JSON fails,
                        // it's a simple string message.
                        const simpleStringMessage = dataToProcess; // Keep the original string
                        console.warn('WebSocket data.message is a non-JSON string:', simpleStringMessage);

                        if (simpleStringMessage.toLowerCase().includes('select')) {
                            // If the simple string contains "select", treat it as an SQL query
                            setSqlQuery(simpleStringMessage);
                            setTranslationSuccess('SQL query received. Review and execute below.');
                            setIsSqlAreaFlashing(true);
                            setTimeout(() => setIsSqlAreaFlashing(false), 4000);
                            // Set focus on the SQL textarea after receiving a query
                            if (textareaRef.current) {
                                textareaRef.current.focus();
                            }
                            setIsSqlTextareaDisabled(false); // Re-enable SQL textarea
                            
                            setTranslationError(null); // Clear any specific translation error
                            setError(null);            // Clear any general error
                            setResult(null);           // Clear previous results
                            setExecutionSuccess('');   // Clear execution success
                        } else {
                            // Otherwise, treat it as a general translation status message
                            setTranslationSuccess(simpleStringMessage); 
                            setError(null);            // Clear any general error
                            setTranslationError(null); // Clear any specific translation error
                            setResult(null);           // Clear previous results
                            setExecutionSuccess('');   // Clear execution success
                        }
                        return; // Done handling this simple string message
                    }
                }

                // Now, dataToProcess should be an object if it was an object or a parsable JSON string.
                if (typeof dataToProcess === 'object' && dataToProcess !== null) {
                    // Handle successful translation output
                    if (dataToProcess.sql_query !== undefined) {
                        setSqlQuery(dataToProcess.sql_query || ''); // Use empty string if null/undefined
                        if (dataToProcess.sql_query) { // Show success only if query is not empty
                            setTranslationSuccess('SQL translation successful via WebSocket! Execute the query below.');
                            setIsSqlAreaFlashing(true);
                            setTimeout(() => setIsSqlAreaFlashing(false), 4000);
                        } else {
                            setTranslationSuccess(''); // Clear success if query is empty
                        }
                        setIsSqlTextareaDisabled(false); // Re-enable SQL textarea and related buttons
                        setTranslationError(null); // Clear translation error on new query
                        setError(null);             // Clear general error
                        setResult(null);            // Clear previous results
                        setExecutionSuccess('');   // Clear execution success
                    }

                    // Handle query execution result (Note: current execution is HTTP, this might be for a different WS flow)
                    if (dataToProcess.result !== undefined) {
                        setResult(dataToProcess.result);
                        setError(null);             // Clear general error on new results
                        setTranslationError(null);  // Clear translation error
                        setTranslationSuccess('');  // Clear translation success
                        setExecutionSuccess('');   // Clear execution success (or set if WS provides exec status)
                    }

                    // Handle translation error
                    if (dataToProcess.translation_error) {
                        setTranslationError(dataToProcess.translation_error);
                        setTranslationSuccess('');
                        setIsSqlTextareaDisabled(false); // Re-enable UI for another attempt
                        // Note: If sql_query was also present and processed, it would have cleared this.
                        // This assumes translation_error messages are distinct.
                        // No change to executionSuccess here, as it's a translation specific error
                    }

                    // Handle execution error (general error, as per the original comment 'setError')
                    if (dataToProcess.error) {
                        setError(dataToProcess.error);
                        setResult(null);            // Clear results on execution error
                        setTranslationSuccess('');  // Clear any translation success message
                        setExecutionSuccess('');   // Clear execution success
                        setIsSqlTextareaDisabled(false); // Re-enable UI if error might relate to a stuck translation
                    }
                } else {
                    console.warn('WebSocket data.message did not resolve to an object:', dataToProcess);
                    // Optionally, set a generic error if the payload isn't an object as expected
                    // setError('Received unprocessable message content from server.');
                }
            } catch (e) {
                // This catches errors from the initial JSON.parse(event.data) or other unexpected issues.
                console.error('Failed to parse or process WebSocket message:', e);
                setError('Received an invalid message from the server.');
                setTranslationError(null);
                setTranslationSuccess('');
                setResult(null);
                setExecutionSuccess(''); // Clear success message on error
            }
        },
        shouldReconnect: (closeEvent) => true
    });

    useEffect(() => {
        if (naturalLanguageQueryTextareaRef.current) { // Change to focus on the natural language query textarea
            naturalLanguageQueryTextareaRef.current.focus(); // Change to focus on the natural language query textarea
        }
    }, []);

    // Generate highlighted HTML whenever sqlQuery changes
    useEffect(() => {
        // The logic to add a space if the query ends with a newline helps Prism correctly highlight the last line if it's empty.
        const codeToHighlight = sqlQuery + (sqlQuery.endsWith('\n') ? ' ' : '');

        if (Prism.languages.sql) {
            const html = Prism.highlight(codeToHighlight, Prism.languages.sql, 'sql');
            setHighlightedHtml(html);
        } else {
            // Fallback: if SQL grammar isn't loaded, just show plain, escaped text.
            const escapedText = codeToHighlight
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;");
            setHighlightedHtml(escapedText);
        }
    }, [sqlQuery]); // Re-run when sqlQuery changes

    // Synchronize scroll between textarea and pre
    const syncScroll = () => {
        if (textareaRef.current && preRef.current) {
            preRef.current.scrollTop = textareaRef.current.scrollTop;
            preRef.current.scrollLeft = textareaRef.current.scrollLeft;
        }
    };

    const handleTranslateQuery = async () => {
        setIsTranslating(true);
        setIsSqlTextareaDisabled(true); // Disable SQL textarea AND Execute Query button (implicitly via this state)
        setTranslationError(null);
        setTranslationSuccess(''); // Clear previous success message
        setError(null); // Clear SQL execution errors as well
        setResult(null); // Clear previous results
        setExecutionSuccess(''); // Clear execution success message

        const token = localStorage.getItem('optimus');

        if (!token) {
            setTranslationError('Authentication token not found. Please log in.');
            setIsTranslating(false);
            return;
        }

        const trimmedNlQuery = naturalLanguageQuery.trim();
        if (!trimmedNlQuery) {
            setTranslationError('Natural language query cannot be empty.');
            setIsTranslating(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/aiqueryagent/translatequery`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    natural_language_query: trimmedNlQuery,
                    api_key: API_KEY,
                    user_id: userId
                })
            });

            const responseData = await response.json();

            if (!response.ok) {
                // This error is for the initiation request itself.
                throw new Error(responseData.detail || `HTTP error! status: ${response.status}`);
            }

            // If the request to initiate translation was successful,
            // set a message indicating that we are waiting for the WebSocket response.
            setTranslationSuccess(responseData.status);

        } catch (err) {
            // This catches errors from the fetch call itself or if !response.ok
            setTranslationSuccess(''); // Clear any success message
            if (err instanceof Error) {
                setTranslationError(`Failed to initiate translation: ${err.message}`);
            } else {
                setTranslationError(`An unknown error occurred while initiating translation: ${String(err)}`);
            }
            setIsSqlTextareaDisabled(false); // Re-enable UI components on fetch error
        } finally {
            setIsTranslating(false); // Stop the "Translating..." indicator for the button
        }
    };

    const handleExecuteQuery = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        setResult(null);
        setError(null);
        setExecutionSuccess(''); // Clear previous execution success message

        const token = localStorage.getItem('optimus');

        if (!token) {
            setError('Authentication token not found. Please log in.');
            setExecutionSuccess(''); // Ensure clear
            setIsLoading(false);
            return;
        }

        const trimmedQuery = sqlQuery.trim();
        if (!trimmedQuery) {
            setError('SQL query cannot be empty.');
            setExecutionSuccess(''); // Ensure clear
            setIsLoading(false);
            return;
        }

        // Basic client-side check (optional, as backend validates)
        if (!trimmedQuery.toLowerCase().startsWith('select')) {
            setError('Only SELECT queries are allowed.');
            setExecutionSuccess(''); // Ensure clear
            setIsLoading(false);
            return;
        }

        // const getBaseURL = window.location.hostname === '192.168.1.45'
        //     ? 'http://localhost:8383'
        //     : 'https://api.galeforceai.com';

        try {
            const response = await fetch(`${API_BASE_URL}/aiqueryagent/executesqlquery`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    sql_query: trimmedQuery,
                    api_key: API_KEY
                })
            });

            const responseData = await response.json();

            if (!response.ok) {
                // Assuming the API returns error details in a 'detail' field
                throw new Error(responseData.detail || `HTTP error! status: ${response.status}`);
            }

            // Assuming the API returns the query result directly or within a specific structure
            // Adjust if the structure is different - now we just set the raw data
            setResult(responseData); // Directly set the response data
            setExecutionSuccess('Query executed successfully.'); // Set success message
            setError(null); // Clear any error if successful

        } catch (err) {
            // Check if err is an instance of Error (good practice in JS too)
            if (err instanceof Error) {
                setError(`Failed to execute query: ${err.message}`);
            } else {
                // Handle cases where the thrown value might not be an Error object
                setError(`An unknown error occurred: ${String(err)}`);
            }
            setExecutionSuccess(''); // Clear success message on error
        } finally {
            setIsLoading(false);
        }
    };

    // Determine keys for the table header safely
    const tableHeaders = result && result.length > 0 ? Object.keys(result[0]) : [];

    // Helper function to format header keys
    const formatHeader = (headerKey) => {
        if (!headerKey) return '';
        return headerKey
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    return (
        <div>
            <LoginCheck /> {/* Ensures user is logged in */}
            <h1 className='page-header gradient-text'>
                <span className='gradient-text'>AI Query Agent</span>{' '}
                <span className='header-tagline'>Tracir Reporting Database</span>
            </h1>

            {/* Natural Language Query Section */}
            <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>
                    <label htmlFor='naturalLanguageQuery' style={{ display: 'block', marginBottom: '5px', color: 'lightgray', fontWeight: 'bold' }}>
                        Enter Natural Language Query:
                    </label>
                    <textarea
                        id='naturalLanguageQuery'
                        ref={naturalLanguageQueryTextareaRef} // Assign the ref to the natural language query textarea
                        value={naturalLanguageQuery}
                        onChange={(e) => {
                            setNaturalLanguageQuery(e.target.value);
                            setTranslationSuccess(''); // Clear success message on input change
                            setTranslationError(null); // Clear translation error
                            setError(null);            // Clear SQL execution error
                            setExecutionSuccess('');   // Clear SQL execution success
                            setResult(null);           // Clear results
                        }}
                        rows={2}
                        placeholder='Example: How many total apps are there? (This feature is not fully ready and might be buggy.)'
                        spellCheck='false'
                        style={{
                            width: '100%',
                            display: 'block',
                            padding: '12px',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            backgroundColor: '#2d2d2d',
                            color: 'lightgray',
                            fontFamily: "Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace",
                            fontSize: '1rem',
                            lineHeight: '1.5',
                            boxSizing: 'border-box',
                            resize: 'none',
                        }}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', alignSelf: 'flex-start' }}>
                    <button
                        type='button' // Important: type='button' to prevent form submission
                        onClick={handleTranslateQuery}
                        disabled={isTranslating || isSqlTextareaDisabled} // Disabled condition remains the same
                        style={{
                            padding: '10px 20px',
                            backgroundColor: (isTranslating || isSqlTextareaDisabled) ? '#555' : '#28a745', // Green background, gray when disabled
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: (isTranslating || isSqlTextareaDisabled) ? 'default' : 'pointer', // Updated cursor condition
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            transition: 'background-color 0.2s ease',
                            width: '170px', // Added fixed width
                            // alignSelf: 'flex-start', // Moved to parent div
                        }}
                        onMouseOver={e => { if (!(isTranslating || isSqlTextareaDisabled)) e.currentTarget.style.backgroundColor = '#218838'; }} // Updated hover condition
                        onMouseOut={e => { if (!(isTranslating || isSqlTextareaDisabled)) e.currentTarget.style.backgroundColor = '#28a745'; }} // Updated hover condition
                    >
                        {isSqlTextareaDisabled ? 'Translating...' : 'Translate to SQL'}
                    </button>
                    {translationSuccess && (
                        <span style={{ color: 'lightgreen', fontSize: '0.9rem' /* Optionally adjust font size */, backgroundColor: '#224422', padding: '5px 8px', borderRadius: '4px' }}>
                            {translationSuccess}
                        </span>
                    )}
                    {translationError && (
                        <span style={{ color: '#ff4d4d', fontSize: '0.9rem', backgroundColor: '#442222', padding: '5px 8px', borderRadius: '4px' }}>
                            Translation Error: {translationError}
                        </span>
                    )}
                </div>
            </div>

            <form onSubmit={handleExecuteQuery} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div>
                    <label htmlFor='sqlQuery' style={{ display: 'block', marginBottom: '5px', color: 'lightgray', fontWeight: 'bold' }}>
                        SELECT SQL Query Only:
                    </label>
                    <div style={{ position: 'relative', width: '100%' }}> {/* Container for positioning */}
                        <textarea
                            ref={textareaRef}
                            id='sqlQuery'
                            value={sqlQuery}
                            onChange={(e) => {
                                setSqlQuery(e.target.value);
                                setTranslationSuccess(''); // Clear messages related to translation
                                setTranslationError(null);
                                setError(null);            // Clear previous execution status
                                setExecutionSuccess('');   // Clear previous execution success
                                setResult(null);           // Clear results as query is changing
                            }}
                            onScroll={syncScroll} // Sync scroll
                            rows={8}
                            placeholder='Example: SELECT * FROM applications'
                            spellCheck='false' // Disable spellcheck for code
                            disabled={isSqlTextareaDisabled} // Control disabled state
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%', // Ensure textarea fills the pre area
                                display: 'block',
                                padding: '12px',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                backgroundColor: isSqlTextareaDisabled ? '#3a3a3a' : 'transparent', // Change background when disabled
                                color: 'transparent', // Make text transparent
                                caretColor: 'lightgray', // Keep caret visible
                                fontFamily: "Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace", // Match Prism theme
                                fontSize: '1rem',
                                lineHeight: '1.5',
                                fontWeight: 'normal', // Normalize
                                letterSpacing: 'normal', // Normalize
                                wordSpacing: 'normal', // Normalize
                                tabSize: 4, // Normalize
                                boxSizing: 'border-box',
                                zIndex: 1, // Textarea on top
                                resize: 'none', // Optional: disable textarea resize
                                whiteSpace: 'pre',
                                overflow: 'auto',
                            }}
                        />
                        <pre
                            ref={preRef}
                            aria-hidden='true' // Hide from screen readers
                            style={{
                                width: '100%',
                                margin: 0, // Remove default pre margin
                                padding: '12px',
                                border: '1px solid #444',
                                borderRadius: '4px',
                                backgroundColor: '#2d2d2d',
                                fontFamily: "Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace", // Match Prism theme
                                fontSize: '1rem',
                                lineHeight: '1.5',
                                fontWeight: 'normal', // Normalize
                                letterSpacing: 'normal', // Normalize
                                wordSpacing: 'normal', // Normalize
                                tabSize: 4, // Normalize
                                boxSizing: 'border-box',
                                minHeight: 'calc(8 * 1.5rem + 24px)',
                                whiteSpace: 'pre',
                                overflow: 'auto',
                                zIndex: 0, // Pre behind textarea
                                boxShadow: isSqlAreaFlashing ? '0 0 8px 3px #32CD32' : 'none', // LimeGreen glow
                                transition: 'box-shadow 0.25s ease-in-out', // Smooth transition for the glow
                            }}
                        >
                            <code 
                                className='language-sql'
                                dangerouslySetInnerHTML={{ __html: highlightedHtml }}
                            />
                        </pre>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', alignSelf: 'flex-start' }}>
                    <button
                        type='submit'
                        disabled={isLoading || isSqlTextareaDisabled} // Updated disabled condition
                        style={{
                            padding: '10px 20px',
                            backgroundColor: (isLoading || isSqlTextareaDisabled) ? '#555' : '#007bff', // Blue background, gray when disabled
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: (isLoading || isSqlTextareaDisabled) ? 'default' : 'pointer', // Pointer cursor
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            transition: 'background-color 0.2s ease', // Smooth transition
                            // alignSelf: 'flex-start', // Moved to parent div
                            width: '170px', // Added fixed width
                        }}
                        // Add a simple hover effect inline (more complex effects better with CSS classes)
                        onMouseOver={e => { if (!(isLoading || isSqlTextareaDisabled)) e.currentTarget.style.backgroundColor = '#0056b3'; }}
                        onMouseOut={e => { if (!(isLoading || isSqlTextareaDisabled)) e.currentTarget.style.backgroundColor = '#007bff'; }}
                    >
                        {isLoading ? 'Executing...' : 'Execute Query'}
                    </button>
                    {error && (
                        <span style={{ color: '#ff4d4d', fontSize: '0.9rem', backgroundColor: '#442222', padding: '5px 8px', borderRadius: '4px' }}>
                            Error: {error}
                        </span>
                    )}
                    {executionSuccess && !error && (
                        <span style={{ color: 'lightgreen', fontSize: '0.9rem', backgroundColor: '#224422', padding: '5px 8px', borderRadius: '4px' }}>
                            {executionSuccess}
                        </span>
                    )}
                </div>
            </form>

            {/* Adjust result rendering based on the assumption that result is now the array directly */}
            {result && (
                <div style={{ marginTop: '20px' }}>
                    <h4>
                        Query Results
                        {result.length === 1 ? ':' : 
                         result.length === 0 ? ' (0 rows):' :
                         result.length === 100 ? ` (showing 100 rows out of potentially more):` :
                         ` (${result.length} rows):`
                        }
                    </h4>
                    {result && result.length > 0 ? (
                        <table style={{ borderCollapse: 'collapse', border: '1px solid darkgray', minWidth: '285px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#003366' }}>
                                    {/* Add an empty header for the counter column */}
                                    <th style={{ border: '1px solid darkgray', padding: '5px', color: 'lightgray' }}></th>
                                    {/* Use determined headers */}
                                    {tableHeaders.map(key => <th key={key} style={{ border: '1px solid darkgray', padding: '5px', color: 'lightgray' }}>{formatHeader(key)}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {result.map((row, index) => (
                                    <tr key={index}>
                                        {/* Add a cell for the counter */}
                                        <td style={{ border: '1px solid darkgray', padding: '5px', color: 'lightgray', textAlign: 'center' }}>{index + 1}</td>
                                        {/* Iterate over headers to ensure consistent cell order */}
                                        {tableHeaders.map((key, cellIndex) => (
                                            <td key={cellIndex} style={{ border: '1px solid darkgray', padding: '5px', color: 'lightgray' }}>{typeof row[key] === 'object' ? JSON.stringify(row[key]) : String(row[key])}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        // Check if result is an empty array or just not an array/empty object
                        <p style={{ color: 'lightgray' }}>Query executed successfully, but returned no data.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default AIQueryAgent; 